import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { format } from 'date-fns';
import SessionDatesCalendar from 'components/session/fields/SessionDatesCalendar';

const HINT_SEEN_KEY = 'otx_multidate_hint_seen';

const fmtHour = (hour) => format(new Date(2000, 0, 1, hour), 'hh aa');

const fmtChip = (date) => {
  try {
    return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
};

const goldChipSx = {
  color: 'gold.main',
  border: '1px solid',
  borderColor: 'gold.main',
  backgroundColor: 'transparent',
  fontWeight: 700,
  '& .MuiChip-deleteIcon': { color: 'gold.main' },
};

/**
 * Fila de un horario: hora + entrenador + contador + chips de fechas desplegables.
 * El horario ACTIVO (el que edita el FORMULARIO) va resaltado en dorado.
 */
function BlockRow({
  label,
  hourLabel,
  trainerName = null,
  count,
  active,
  onClick,
  onRemove = null,
  expanded,
  onToggleExpand,
  children = null,
  removeLabel = ''
}) {
  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: active ? 'gold.main' : 'hairline.main',
        backgroundColor: active ? 'rgba(249,189,78,0.08)' : 'surface.content',
        transition: 'border-color 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms cubic-bezier(0.32,0.72,0,1)',
      }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={onClick}
        role="button"
        aria-pressed={active}
        sx={{ px: 1.25, py: 0.75, cursor: 'pointer', minHeight: 46, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: active ? 'gold.main' : 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {hourLabel}
        </Typography>
        {trainerName && (
          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            · {trainerName}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {count > 0 && (
          <Chip size="small" label={count} sx={{ backgroundColor: 'rgba(249,189,78,0.14)', color: 'gold.main', fontWeight: 800 }} />
        )}
        {onRemove && (
          <Tooltip title={removeLabel}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              aria-label={removeLabel}
              sx={{ color: 'text.secondary', '&:hover': { color: '#ff8a8a' } }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          aria-label={expanded ? 'colapsar fechas' : 'ver fechas'}
          sx={{
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)',
          }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1.25 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

BlockRow.propTypes = {
  label: PropTypes.string.isRequired,
  hourLabel: PropTypes.string.isRequired,
  trainerName: PropTypes.string,
  count: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  expanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  children: PropTypes.node,
  removeLabel: PropTypes.string,
};

/**
 * Panel "Programar varias fechas" — UN SOLO SISTEMA con el formulario:
 * 1) eliges Fecha + hora en el form → aparecen los entrenadores de esa fecha/hora;
 * 2) eliges ENTRENADOR → el calendario se habilita mostrando SOLO sus días a esa hora;
 * 3) marcas más fechas para ese horario;
 * 4) "Agregar otro horario" CONGELA el horario actual (fila con hora + entrenador +
 *    chips) y limpia el formulario (con loading dorado) para repetir el flujo.
 * Las fechas ya agendadas a una hora quedan deshabilitadas en el calendario para esa
 * misma hora (aunque sea otro entrenador). Sin dropdowns en las filas.
 */
function MultiDatePanel({
  open,
  onToggleOpen,
  disabled = false,
  disabledReason = null,
  primaryDate = null,
  primaryFromHour = null,
  primaryToHour = null,
  trainerId = null,
  trainerName = null,
  sessionTypeId = null,
  language = '',
  extraDates,
  onTogglePrimaryBlockDate,
  blocks,
  onRemoveBlock,
  onRemoveBlockDate,
  onAddBlock,
  canAddBlock = false,
  transitioning = false,
  takenDates = null
}) {
  const { t } = useTranslation(['sessions']);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [hintOpen, setHintOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState('active');

  // Hint de descubrimiento (una sola vez por navegador): el panel se puede plegar.
  useEffect(() => {
    if (!window.localStorage.getItem(HINT_SEEN_KEY)) {
      setHintOpen(true);
      const timer = setTimeout(() => {
        setHintOpen(false);
        window.localStorage.setItem(HINT_SEEN_KEY, '1');
      }, 6000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const dismissHint = () => {
    if (hintOpen) {
      setHintOpen(false);
      window.localStorage.setItem(HINT_SEEN_KEY, '1');
    }
  };

  const hasHour = Number.isInteger(primaryFromHour);
  const hasTrainer = Boolean(trainerId);
  const calendarDisabled = disabled || !hasHour || !hasTrainer;

  // Razón visible de por qué el calendario sigue deshabilitado (guía del flujo).
  let calendarHint = disabledReason;
  if (!calendarHint && !disabled) {
    if (!hasHour) {
      calendarHint = t('multiDateNeedsTime', {
        ns: 'sessions',
        defaultValue: 'Elige una fecha y una hora para ver los entrenadores disponibles.',
      });
    } else if (!hasTrainer) {
      calendarHint = t('multiDateNeedsTrainer', {
        ns: 'sessions',
        defaultValue: 'Selecciona el entrenador para habilitar el calendario con SUS días disponibles.',
      });
    }
  }

  const activeHourLabel = hasHour
    ? `${fmtHour(primaryFromHour)} - ${fmtHour(primaryToHour ?? primaryFromHour + 1)}`
    : '—';

  const panelBody = (
    <Stack spacing={1.5} sx={{ minWidth: { lg: 320 }, position: 'relative' }}>
      {/* Paso a paso del flujo (numerado, legible de un vistazo). */}
      <Stack spacing={0.75} component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {[1, 2, 3, 4].map((step) => (
          <Stack key={step} component="li" direction="row" spacing={1} alignItems="flex-start">
            <Box
              aria-hidden
              sx={{
                alignItems: 'center',
                backgroundColor: 'gold.soft',
                borderRadius: '50%',
                color: 'gold.main',
                display: 'flex',
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 800,
                height: 18,
                justifyContent: 'center',
                mt: '1px',
                width: 18,
              }}>
              {step}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {t(`multiDateStep${step}`, { ns: 'sessions' })}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {calendarHint && (
        <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 700 }}>
          {calendarHint}
        </Typography>
      )}

      {/* Calendario ÚNICO: muestra los días del ENTRENADOR elegido a la hora del form. */}
      <SessionDatesCalendar
        primaryDate={primaryDate}
        extraDates={extraDates}
        onToggle={onTogglePrimaryBlockDate}
        sessionTypeId={sessionTypeId}
        language={language}
        fromHour={primaryFromHour}
        toHour={primaryToHour}
        trainerId={trainerId}
        takenDates={takenDates}
        disabled={calendarDisabled}
      />

      {/* Horarios congelados (hora + entrenador + fechas). */}
      {blocks.map((block, index) => {
        const key = `block-${block.id}`;
        return (
          <BlockRow
            key={block.id}
            label={`${t('timeBlock', { ns: 'sessions', defaultValue: 'Horario' })} ${index + 1}`}
            hourLabel={`${fmtHour(block.fromHour)} - ${fmtHour(block.toHour)}`}
            trainerName={block.trainerName}
            count={block.dates.length}
            active={false}
            onClick={() => setExpandedKey(expandedKey === key ? null : key)}
            onRemove={() => onRemoveBlock(block.id)}
            removeLabel={t('removeTimeBlock', { ns: 'sessions', defaultValue: 'Quitar horario' })}
            expanded={expandedKey === key}
            onToggleExpand={() => setExpandedKey(expandedKey === key ? null : key)}>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {block.dates.map((d) => (
                <Chip
                  key={d.toISOString()}
                  size="small"
                  label={`${fmtChip(d)} · ${fmtHour(block.fromHour)}`}
                  onDelete={() => onRemoveBlockDate(block.id, d)}
                  sx={goldChipSx}
                />
              ))}
            </Stack>
          </BlockRow>
        );
      })}

      {/* Horario ACTIVO: refleja el formulario (fecha/hora/entrenador actuales). */}
      <BlockRow
        label={`${t('timeBlock', { ns: 'sessions', defaultValue: 'Horario' })} ${blocks.length + 1}`}
        hourLabel={activeHourLabel}
        trainerName={hasTrainer ? trainerName : t('multiDatePickTrainer', { ns: 'sessions', defaultValue: 'elige entrenador' })}
        count={(primaryDate ? 1 : 0) + extraDates.length}
        active
        onClick={() => setExpandedKey(expandedKey === 'active' ? null : 'active')}
        expanded={expandedKey === 'active'}
        onToggleExpand={() => setExpandedKey(expandedKey === 'active' ? null : 'active')}>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {primaryDate && hasHour && (
            <Chip
              size="small"
              label={`${fmtChip(primaryDate)} · ${fmtHour(primaryFromHour)}`}
              sx={{ backgroundColor: 'gold.main', color: '#111418', fontWeight: 800 }}
            />
          )}
          {extraDates.map((d) => (
            <Chip
              key={d.toISOString()}
              size="small"
              label={hasHour ? `${fmtChip(d)} · ${fmtHour(primaryFromHour)}` : fmtChip(d)}
              onDelete={() => onTogglePrimaryBlockDate(d)}
              sx={goldChipSx}
            />
          ))}
          {!primaryDate && extraDates.length === 0 && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('noDatesYet', { ns: 'sessions', defaultValue: 'Sin fechas todavía: márcalas en el calendario.' })}
            </Typography>
          )}
        </Stack>
      </BlockRow>

      <Tooltip
        title={canAddBlock ? '' : t('addTimeBlockHint', {
          ns: 'sessions',
          defaultValue: 'Completa fecha, hora y entrenador del horario actual para agregar otro.',
        })}>
        <span>
          <Button
            startIcon={<AddIcon />}
            onClick={onAddBlock}
            disabled={disabled || !canAddBlock || transitioning}
            sx={{
              alignSelf: 'flex-start',
              color: 'gold.main',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(249,189,78,0.08)' },
              '&.Mui-disabled': { color: 'text.disabled' },
            }}>
            {t('addTimeBlock', { ns: 'sessions', defaultValue: 'Agregar otro horario' })}
          </Button>
        </span>
      </Tooltip>

      {/* Transición de limpieza entre horarios: loading dorado sobre el panel. */}
      {transitioning && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.45)',
            borderRadius: '14px',
          }}>
          <CircularProgress size={26} sx={{ color: 'gold.main' }} />
        </Box>
      )}
    </Stack>
  );

  return (
    <Stack spacing={1.25}>
      {/* Header colapsable */}
      <Tooltip
        title={t('collapseHint', {
          ns: 'sessions',
          defaultValue: 'Puedes plegar o desplegar este panel cuando quieras.',
        })}
        open={hintOpen}
        arrow
        placement="top-end">
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => { dismissHint(); onToggleOpen(); }}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          role="button"
          aria-expanded={open}
          aria-label={t('scheduleMultiple', { ns: 'sessions' })}>
          <EventOutlinedIcon sx={{ fontSize: 18, color: 'gold.main' }} />
          {(open || !isDesktop) && (
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 800, flex: 1 }}>
              {t('scheduleMultiple', { ns: 'sessions' })}
            </Typography>
          )}
          <IconButton
            size="small"
            aria-label={open ? 'colapsar' : 'expandir'}
            sx={{
              color: 'text.secondary',
              // Desktop: chevron lateral (panel horizontal); mobile: chevron vertical.
              transform: (() => {
                if (isDesktop) return `rotate(${open ? -90 : 90}deg)`;
                return open ? 'rotate(180deg)' : 'none';
              })(),
              transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)',
            }}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Tooltip>

      {/* Desktop: despliegue horizontal (derecha → izquierda); mobile: vertical. */}
      <Collapse in={open} orientation={isDesktop ? 'horizontal' : 'vertical'} unmountOnExit>
        {panelBody}
      </Collapse>
    </Stack>
  );
}

MultiDatePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggleOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  primaryDate: PropTypes.instanceOf(Date),
  primaryFromHour: PropTypes.number,
  primaryToHour: PropTypes.number,
  trainerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  trainerName: PropTypes.string,
  sessionTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  language: PropTypes.string,
  extraDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  onTogglePrimaryBlockDate: PropTypes.func.isRequired,
  blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRemoveBlock: PropTypes.func.isRequired,
  onRemoveBlockDate: PropTypes.func.isRequired,
  onAddBlock: PropTypes.func.isRequired,
  canAddBlock: PropTypes.bool,
  transitioning: PropTypes.bool,
  takenDates: PropTypes.instanceOf(Set),
};

export default MultiDatePanel;
